<template>
    <filters
        v-model="filters"
        @fetch="reviews = []; pagination.current_page = 1; fetch()"
        :loading="loading"
        search-filter
        search-label="E-mail ou ID do produtor"
        status-filter
        :statuses="[
            { label: 'Aberto', value: 'open' },
            { label: 'Fechado', value: 'closed' },
        ]"
    />

    <!-- Filtros extras (responsavel, mes, regra) -->
    <div class="mb-4 flex flex-wrap items-end gap-3">
        <div>
            <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Responsável</label>
            <select
                v-model="extraFilters.assignee_id"
                class="block w-48 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
                <option value="">Todos</option>
                <option v-for="user in assignedUsers" :key="user.id" :value="user.id">{{ user.name }}</option>
            </select>
        </div>
        <div>
            <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Mês de referência</label>
            <select
                v-model="extraFilters.reference_month"
                class="block w-40 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
                <option value="">Todos</option>
                <option v-for="month in availableMonths" :key="month" :value="month">{{ formatMonth(month) }}</option>
            </select>
        </div>
        <div>
            <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Regra</label>
            <select
                v-model="extraFilters.rule"
                class="block w-64 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
                <option value="">Todas</option>
                <option v-for="rule in availableRules" :key="rule" :value="rule">{{ rule }}</option>
            </select>
        </div>
        <button
            @click="reviews = []; pagination.current_page = 1; fetch()"
            class="btn-primary-sm"
            :disabled="loading"
        >
            Aplicar
        </button>
    </div>

    <div class="mb-3 flex">
        <div class="ml-auto flex items-center text-sm text-gray-500 dark:text-gray-400">
            Exibindo {{ pagination.from }} a {{ pagination.to }} de {{ pagination.total }} registros
        </div>
    </div>

    <div v-if="loading" class="flex justify-center py-12">
        <ticto-loading />
    </div>

    <div v-else-if="reviews.length === 0" class="text-center py-12 text-gray-500 dark:text-gray-400">
        Nenhuma revisão encontrada.
    </div>

    <table-component
        v-else
        :loading="loading"
        :headers="[
            'Produtor',
            'Mês Ref.',
            'Autorizadas',
            'Chargebacks',
            'Taxa %',
            'Regra',
            'Status',
            'Responsável',
            'Aberto em',
            'Última Interação',
        ]"
        :pagination="pagination"
        @fetch="fetch"
    >
        <tr
            v-for="review in reviews"
            :key="review.id"
            class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
            @click="$router.push({ name: 'finnancial.chargebacks.review', params: { review_id: review.id } })"
        >
            <td class="table-cell-sm">
                <div class="font-medium text-gray-900 dark:text-white">{{ review.producer_name || '-' }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-400">{{ review.email }}</div>
            </td>
            <td class="table-cell-sm">{{ review.reference_month_display }}</td>
            <td class="table-cell-sm">{{ review.total_authorized.toLocaleString('pt-BR') }}</td>
            <td class="table-cell-sm">{{ review.total_chargeback.toLocaleString('pt-BR') }}</td>
            <td class="table-cell-sm">
                <span
                    class="text-sm font-semibold px-2 py-1 rounded"
                    :class="rateClass(review.pct_chargeback)"
                >
                    {{ review.pct_chargeback.toFixed(2).replace('.', ',') }}%
                </span>
            </td>
            <td class="table-cell-sm text-xs">{{ review.rule }}</td>
            <td class="table-cell-sm">
                <span
                    class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                    :class="statusClass(review.status)"
                >
                    {{ review.status === 'open' ? 'Aberto' : 'Fechado' }}
                </span>
            </td>
            <td class="table-cell-sm text-xs text-gray-500 dark:text-gray-400">
                {{ review.assignee_name || 'Sem responsável' }}
            </td>
            <td class="table-cell-sm text-xs text-gray-500 dark:text-gray-400">
                {{ review.created_at }}
            </td>
            <td class="table-cell-sm text-xs text-gray-500 dark:text-gray-400">
                <template v-if="review.last_interaction">
                    <span class="block">{{ review.last_interaction.timestamp }}</span>
                    <span class="text-gray-400 dark:text-gray-500">{{ interactionTypeLabel(review.last_interaction.interaction_type) }}</span>
                </template>
                <span v-else>-</span>
            </td>
        </tr>
    </table-component>
</template>

<script>
import Filters from "../../../../components/layouts/Filters.vue";
import TableComponent from "../../../../components/layouts/table/TableComponent.vue";
import PaginationObject from "../../../../mixins/PaginationObject.vue";
import TictoLoading from "../../../../components/layouts/TictoLoading.vue";

export default {
    components: { Filters, TableComponent, TictoLoading },
    mixins: [PaginationObject],

    data() {
        return {
            reviews: [],
            filters: {
                text: "",
                status: [],
            },
            extraFilters: {
                assignee_id: "",
                reference_month: "",
                rule: "",
            },
            loading: false,
            assignedUsers: [],
            availableMonths: [],
            availableRules: [
                '1-5 vendas, >= 1 CB',
                '6-19 vendas, >= 1 CB',
                '6-19 vendas, >= 2 CBs',
                '20-30 vendas, >= 2 CBs',
                '20-30 vendas, >= 3 CBs',
                '31-99 vendas, >= 3 CBs',
                '31-99 vendas, >= 4 CBs',
                '100+ vendas, taxa > 1.2%',
                '100+ vendas, taxa > 2%',
            ],
        };
    },

    mounted() {
        this.fetch();
        this.fetchAssignedUsers();
        this.generateMonths();
    },

    methods: {
        fetch() {
            this.loading = true;

            const params = {
                ...this.filters,
                page: this.pagination.current_page,
            };

            if (this.extraFilters.assignee_id) {
                params['filter[assignee_id]'] = this.extraFilters.assignee_id;
            }
            if (this.extraFilters.reference_month) {
                params['filter[reference_month]'] = this.extraFilters.reference_month;
            }
            if (this.extraFilters.rule) {
                params['filter[rule]'] = this.extraFilters.rule;
            }

            axios
                .get("/api/chargeback-reviews", { params })
                .then(({ data }) => {
                    this.reviews = data.data;
                    this.setPagination(data);
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                });
        },

        fetchAssignedUsers() {
            axios.get("/api/chargeback-reviews/assigned-users").then(({ data }) => {
                this.assignedUsers = data;
            }).catch(() => {});
        },

        generateMonths() {
            const months = [];
            const now = new Date();
            for (let i = 0; i < 6; i++) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                months.push(`${y}-${m}`);
            }
            this.availableMonths = months;
        },

        formatMonth(month) {
            const [y, m] = month.split('-');
            return `${m}/${y}`;
        },

        rateClass(rate) {
            if (rate > 2) return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
            if (rate > 1.2) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
            return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
        },

        statusClass(status) {
            if (status === "open") return "bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/20 dark:text-green-400 dark:ring-green-500/30";
            return "bg-gray-50 text-gray-600 ring-gray-500/10 dark:bg-gray-700 dark:text-gray-400 dark:ring-gray-600";
        },

        interactionTypeLabel(type) {
            const labels = {
                comment: 'Comentário',
                notification: 'Notificação',
                stage_change: 'Mudança de status',
            };
            return labels[type] || type;
        },
    },
};
</script>
